import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const LiveChatWidget = () => {
  const { t, currentLanguage } = useLanguage();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const chatEndRef = useRef(null);

  // Initialize messages with greeting when component mounts or language changes
  useEffect(() => {
    setMessages([{ from: 'bot', text: t('chat-greeting') }]);
  }, [currentLanguage, t]);

  const exampleReplies = [
    t('chat-reply-1'),
    t('chat-reply-2'),
    t('chat-reply-3'),
    t('chat-reply-4')
  ];

  useEffect(() => {
    if (open && chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open]);

  const handleSend = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setMessages((msgs) => [
      ...msgs,
      { from: 'user', text: input }
    ]);
    setInput('');
    // Simulate bot reply
    setTimeout(() => {
      const reply = exampleReplies[Math.floor(Math.random() * exampleReplies.length)];
      setMessages((msgs) => [
        ...msgs,
        { from: 'bot', text: `${t('chat-ai-assistant')}: ${reply}` }
      ]);
    }, 900);
  };

  return (
    <>
      {/* Floating Button */}
      <button
        className="fixed z-50 bottom-6 right-6 bg-green-600 hover:bg-green-700 text-white rounded-full shadow-lg p-4 flex items-center justify-center transition-all duration-200 focus:outline-none"
        style={{ boxShadow: '0 4px 24px rgba(76,175,80,0.25)' }}
        onClick={() => setOpen(true)}
        aria-label={t('chat-title')}
        tabIndex={0}
        hidden={open}
      >
        <MessageCircle className="h-7 w-7" />
      </button>

      {/* Chat Popup */}
      {open && (
        <div className="fixed z-50 bottom-6 right-6 w-80 max-w-[95vw] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-fade-in border border-green-200">
          {/* Header */}
          <div className="flex items-center justify-between bg-green-600 px-4 py-3">
            <div className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-white" />
              <span className="text-white font-bold text-base">{t('chat-title')}</span>
            </div>
            <button onClick={() => setOpen(false)} className="text-white hover:text-green-200 transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>
          {/* Messages */}
          <div className="flex-1 px-4 py-3 space-y-2 overflow-y-auto bg-neutral-50" style={{ minHeight: 200, maxHeight: 320 }}>
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`px-4 py-2 rounded-xl text-sm max-w-[80%] ${msg.from === 'user' ? 'bg-green-100 text-green-900' : 'bg-white text-green-800 border border-green-100'}`}>
                  {msg.text}
                </div>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
          {/* Input */}
          <form onSubmit={handleSend} className="flex items-center gap-2 px-4 py-3 bg-white border-t">
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              className="flex-1 px-3 py-2 rounded-lg border border-green-200 focus:ring-2 focus:ring-green-400 outline-none text-green-900 text-sm"
              placeholder={t('chat-placeholder')}
              autoFocus
            />
            <button type="submit" className="bg-green-600 hover:bg-green-700 text-white rounded-full p-2 transition-all">
              <Send className="h-5 w-5" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default LiveChatWidget; 